import axios, { AxiosInstance, AxiosResponse } from 'axios'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'

class ApiService {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 5000, // 5秒タイムアウト
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // リクエストインターセプター（認証トークン追加）
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken')
        if (token && token !== 'undefined' && token !== 'null') {
          // 有効なトークンのみでAuthorizationヘッダーを追加
          config.headers.Authorization = `Bearer ${token}`
        }
        console.log('🌐 API リクエスト:', {
          url: config.url,
          method: config.method,
          hasToken: !!token && token !== 'undefined' && token !== 'null',
          tokenPreview: token ? `${token.substring(0, 20)}...` : 'none',
          authHeader: config.headers.Authorization,
        })
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // レスポンスインターセプター（エラーハンドリング）
    this.api.interceptors.response.use(
      (response) => {
        console.log('✅ API レスポンス成功:', {
          url: response.config.url,
          status: response.status,
        })
        return response
      },
      (error) => {
        console.error('API レスポンスエラー:', {
          url: error.config?.url,
          status: error.response?.status,
          message: error.message,
        })

        // ネットワークエラーの場合
        if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
          console.warn('バックエンドAPIに接続できません')
        }

        // 認証エラーの処理
        if (error.response?.status === 401) {
          // 招待受付ページ、パスワードリセット、ログインページでは401リダイレクトを行わない
          const currentPath = window.location.pathname
          const excludedPaths = ['/invite/accept', '/reset-password', '/login', '/register']
          const shouldSkipRedirect = excludedPaths.some((path) => currentPath.startsWith(path))

          if (!shouldSkipRedirect) {
            console.log('認証エラー - ログインページにリダイレクト')
            localStorage.removeItem('authToken')
            localStorage.removeItem('user')
            window.location.href = '/login'
          } else {
            console.log('認証エラー - 除外パスのためリダイレクトをスキップ:', currentPath)
          }
        }

        return Promise.reject(error)
      }
    )
  }

  // 汎用APIメソッド
  async get<T>(url: string, params?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.api.get(url, { params })
    return response.data
  }

  async post<T>(url: string, data?: any, config?: { timeout?: number }): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, data, config)
    return response.data
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.api.put(url, data)
    return response.data
  }

  async delete<T>(url: string, config?: { data?: any }): Promise<T> {
    const response: AxiosResponse<T> = await this.api.delete(url, config)
    return response.data
  }

  // ファイルアップロード用
  async upload<T>(url: string, formData: FormData, config?: { timeout?: number }): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // CSV処理のような時間のかかる処理用のタイムアウト設定
      timeout: config?.timeout || 60000, // デフォルト60秒（CSV処理対応）
      ...config,
    })
    return response.data
  }

  // Blobダウンロード用
  async downloadBlob(url: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(url, {
      responseType: 'blob',
    })
    return response.data
  }

  // トークン設定
  setAuthToken(token: string) {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  // トークン削除
  clearAuthToken() {
    delete this.api.defaults.headers.common['Authorization']
  }
}

const apiService = new ApiService()
export { apiService }
export default apiService
